import { io, Socket } from 'socket.io-client';
import { WS_BASE_URL } from '../config/api';

class WebSocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<Function>> = new Map();
  private currentNamespace: string = '';
  private reconnectAttempts: number = 0;

  private reconnectDelay: number = 1000;
  private connectionPromise: Promise<void> | null = null;

  async connect(namespace: string = ''): Promise<void> {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    if (this.socket?.connected && this.currentNamespace === namespace) {
      return Promise.resolve();
    }

    this.connectionPromise = this._connectInternal(namespace);

    try {
      await this.connectionPromise;
    } finally {
      this.connectionPromise = null;
    }
  }

  private async _connectInternal(namespace: string): Promise<void> {
    if (this.socket && this.currentNamespace !== namespace) {
      this.disconnect();
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      if (import.meta.env.DEV) {
        console.warn('No access token found, cannot connect WebSocket');
      }
      return;
    }

    let url = WS_BASE_URL;
    if (namespace) {
      const baseUrl = WS_BASE_URL.replace(/\/$/, '');
      url = `${baseUrl}/${namespace}`;
    }

    this.currentNamespace = namespace;

    this.socket = io(url, {
      auth: {
        token,
      },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: this.reconnectDelay,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
      forceNew: false,
      autoConnect: true,
    });

    const connectPromise = new Promise<void>((resolve, reject) => {
      const connectTimeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'));
      }, 20000);

      const connectHandler = () => {
        clearTimeout(connectTimeout);
        this.reconnectAttempts = 0;
        if (import.meta.env.DEV) {
          console.log(`WebSocket connected to namespace: ${namespace || 'default'}`);
        }
        this.socket?.off('connect', connectHandler);
        this.socket?.off('connect_error', errorHandler);
        resolve();
      };

      const errorHandler = (error: any) => {
        clearTimeout(connectTimeout);
        this.reconnectAttempts++;
        if (import.meta.env.DEV) {
          console.error('WebSocket connection error:', error.message);
        }
        this.socket?.off('connect', connectHandler);
        this.socket?.off('connect_error', errorHandler);
        if (error.message === 'Authentication failed' || error.message?.includes('401')) {
          this.handleAuthError();
        }
        reject(error);
      };

      if (this.socket) {
        this.socket.on('connect', connectHandler);
        this.socket.on('connect_error', errorHandler);
      }
    });

    await connectPromise;
    this.setupExistingListeners();

    this.socket.on('disconnect', (reason: string) => {
      if (import.meta.env.DEV) {
        console.log(`WebSocket disconnected. Reason: ${reason}`);
      }
      if (reason === 'transport close' || reason === 'client disconnect') {
        if (import.meta.env.DEV) {
          console.log('Transport closed, Socket.IO will attempt auto-reconnect');
        }
      } else if (reason === 'io server disconnect') {
        if (import.meta.env.DEV) {
          console.log('Server disconnected, attempting to refresh token and reconnect...');
        }
        setTimeout(() => {
          this.handleAuthError();
        }, 2000);
      }
    });

    this.socket.on('error', (error: any) => {
      if (import.meta.env.DEV) {
        console.error('WebSocket error:', error);
      }
    });

    this.socket.on('reconnect', (attemptNumber: number) => {
      if (import.meta.env.DEV) {
        console.log(`WebSocket reconnected after ${attemptNumber} attempts`);
      }
      this.reconnectAttempts = 0;
    });

    this.socket.on('reconnect_attempt', (attemptNumber: number) => {
      if (import.meta.env.DEV) {
        console.log(`WebSocket reconnection attempt ${attemptNumber}`);
      }
    });

    this.socket.on('reconnect_failed', () => {
      if (import.meta.env.DEV) {
        console.error('WebSocket reconnection failed after all attempts');
      }
    });
  }

  private async handleAuthError() {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const { apiService } = await import('./api');
          const { API_ENDPOINTS } = await import('../config/api');
          const response: any = await apiService.post(API_ENDPOINTS.AUTH.REFRESH, {
            refresh_token: refreshToken,
          });

          if (response.access_token) {
            localStorage.setItem('access_token', response.access_token);
            if (response.refresh_token) {
              localStorage.setItem('refresh_token', response.refresh_token);
            }
            if (this.currentNamespace) {
              await this.connect(this.currentNamespace);
            }
          }
        } catch (refreshError) {
          if (import.meta.env.DEV) {
            console.error('Failed to refresh token:', refreshError);
          }
        }
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error in handleAuthError:', error);
      }
    }
  }

  private setupExistingListeners() {
    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach((callback) => {
        if (this.socket) {
          this.socket.on(event, callback as (...args: any[]) => void);
        }
      });
    });
  }

  on(event: string, callback: (...args: any[]) => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(callback);

    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  off(event: string, callback?: (...args: any[]) => void) {
    if (callback) {
      this.listeners.get(event)?.delete(callback);
      this.socket?.off(event, callback);
    } else {
      this.listeners.delete(event);
      this.socket?.off(event);
    }
  }

  emit(event: string, data?: any) {
    this.socket?.emit(event, data);
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.listeners.clear();
    this.currentNamespace = '';
    this.reconnectAttempts = 0;
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  getNamespace(): string {
    return this.currentNamespace;
  }

  getConnectionState(): 'connected' | 'connecting' | 'disconnected' {
    if (this.connectionPromise) {
      return 'connecting';
    }
    if (this.socket?.connected) {
      return 'connected';
    }
    return 'disconnected';
  }

  async ensureConnection(namespace: string): Promise<void> {
    if (this.isConnected() && this.currentNamespace === namespace) {
      return;
    }
    await this.connect(namespace);
  }
}

export const wsService = new WebSocketService();

